import type {
  BooleanFilterOperator,
  CustomFilter,
  DateFilterOperator,
  NumericFilterOperator,
  SelectionListFilterOperator,
  StringFilterOperator,
} from '../data-builder.store';
import type {
  ApiColumnItem,
  ApiDeliverable,
  ApiFile,
  ApiSetting,
} from './columns-api-types';
import type { components } from './data-builder.schema';
import type { DataBuilderCondition } from './data-builder-api.types';

type ApiStringFilter =
  components['schemas']['data_builder__schema__dashboard__filters__StringFilter'];
type ApiSelectionListFilter = components['schemas']['SelectionListFilter'];
type ApiNumericFilter =
  components['schemas']['data_builder__schema__dashboard__filters__NumericFilter'];
type ApiDateFilter =
  components['schemas']['data_builder__schema__dashboard__filters__DateFilter'];
type ApiBooleanFilter =
  components['schemas']['data_builder__schema__dashboard__filters__BooleanFilter'];
type ApiFilterGroup = components['schemas']['FilterGroup'];

export type FilterModifier = {
  name: string;
  key: string;
};

export type UnitOfAnalysisOption = {
  value: string;
  label: string;
  condition?: DataBuilderCondition | null;
  description?: string | null;
  selectionListKeys: string[];
};

export type ColumnOption = {
  key: string;
  label: string;
  isDefault: boolean;
  isRequired: boolean;
  condition?: DataBuilderCondition | null;
  description?: string | null;
};

function isColumnItem(
  item: ApiColumnItem
): item is ApiColumnItem & { type: 'column'; key: string } {
  return (
    item.type === 'column' && 'key' in item && typeof item.key === 'string'
  );
}

function hasValidFileKey(file: ApiFile): file is ApiFile & { key: string } {
  return typeof file.key === 'string' && file.key.trim().length > 0;
}

function isApiSetting(item: unknown): item is ApiSetting {
  if (typeof item !== 'object' || item === null || !('type' in item)) {
    return false;
  }

  const { type } = item as { type?: unknown };
  return (
    type === 'toggle' || type === 'selection' || type === 'selection_inline'
  );
}

export function getUnitOfAnalysisOptions(
  deliverable: ApiDeliverable | undefined
): UnitOfAnalysisOption[] {
  const items = deliverable?.unit_of_analysis;
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }
  return items.map((item) => ({
    value: item.name.toLowerCase().replace(/\s+/g, '_'),
    label: item.name,
    condition: item.condition,
    description: item.description,
    selectionListKeys: (item.selection_lists ?? []).map((sl) => sl.key),
  }));
}

export function getAggregationColumnOptions(
  deliverable: ApiDeliverable | undefined
): ColumnOption[] {
  const agg = deliverable?.aggregation_columns;
  const list = agg?.columns;
  if (!Array.isArray(list) || list.length === 0) {
    return [];
  }
  return flattenColumnItems(list);
}

export type AggregationColumnItem =
  | { type: 'column'; option: ColumnOption }
  | {
      type: 'group';
      name: string;
      options: ColumnOption[];
      condition?: DataBuilderCondition | null;
      description?: string | null;
      maxSelected?: number | null;
    };

export function getAggregationColumnItems(
  deliverable: ApiDeliverable | undefined
): AggregationColumnItem[] {
  const agg = deliverable?.aggregation_columns;
  const list = agg?.columns;
  if (!Array.isArray(list) || list.length === 0) {
    return [];
  }
  return toAggregationColumnItems(list);
}

export function getAggregationSupportsCustomColumns(
  deliverable: ApiDeliverable | undefined
): boolean {
  return deliverable?.aggregation_columns?.custom_columns === true;
}

export function getDeliverableSettings(
  deliverable: ApiDeliverable | undefined
): ApiSetting[] {
  const settings = deliverable?.settings;
  if (!Array.isArray(settings) || settings.length === 0) {
    return [];
  }

  return settings.filter(isApiSetting);
}

function toAggregationColumnItems(
  items: ApiColumnItem[]
): AggregationColumnItem[] {
  const out: AggregationColumnItem[] = [];
  for (const item of items) {
    if (isColumnItem(item)) {
      out.push({
        type: 'column',
        option: {
          key: item.key,
          label: item.name,
          isDefault: item.default === true,
          isRequired: item.required === true,
          condition: item.condition,
          description: item.description,
        },
      });
    } else if (item.type === 'group') {
      out.push({
        type: 'group',
        name: item.name,
        options: flattenColumnItems(item.columns ?? []),
        condition: item.condition,
        description: item.description,
        maxSelected: item.max_selected ?? null,
      });
    } else if (item.type === 'set') {
      out.push(...toAggregationColumnItems(item.columns ?? []));
    }
  }
  return out;
}

function flattenColumnItems(items: ApiColumnItem[]): ColumnOption[] {
  const out: ColumnOption[] = [];
  for (const item of items) {
    if (isColumnItem(item)) {
      out.push({
        key: item.key,
        label: item.name,
        isDefault: item.default === true,
        isRequired: item.required === true,
        condition: item.condition,
        description: item.description,
      });
    } else if (item.type === 'group' || item.type === 'set') {
      const nested = item.columns ?? [];
      out.push(...flattenColumnItems(nested));
    }
  }
  return out;
}

export function getColumnOptionsFromFile(
  file: { columns?: { columns?: ApiColumnItem[] } | null } | undefined
): ColumnOption[] {
  const list = file?.columns?.columns;
  if (!Array.isArray(list)) {
    return [];
  }
  return flattenColumnItems(list);
}

export function getColumnItemsFromFile(
  file: { columns?: { columns?: ApiColumnItem[] } | null } | undefined
): AggregationColumnItem[] {
  const list = file?.columns?.columns;
  if (!Array.isArray(list)) {
    return [];
  }
  return toAggregationColumnItems(list);
}

function hasFilters(filters: unknown): boolean {
  return Array.isArray(filters) && filters.length > 0;
}

export function getDeliverableHasGeneralFilters(
  deliverable: ApiDeliverable | undefined
): boolean {
  return hasFilters(deliverable?.filters);
}

export function getFileHasFilters(
  file: ApiDeliverable['files'][number] | undefined
): boolean {
  return hasFilters(file?.filters);
}

export function getFileSupportsCustomColumns(
  file: ApiDeliverable['files'][number] | undefined
): boolean {
  return file?.columns?.custom_columns === true;
}

export type FileSection = {
  id: string;
  label: string;
  isDefault: boolean;
  columnOptions: ColumnOption[];
  columnItems: AggregationColumnItem[];
  condition?: DataBuilderCondition | null;
  hasFilters: boolean;
  supportsCustomColumns: boolean;
  customFilterDefinitions: CustomFilterDefinition[];
  customFilterCategories: CustomFilterCategory[];
};

export function getFileSections(
  deliverable: ApiDeliverable | undefined
): FileSection[] {
  const files = deliverable?.files;
  if (!Array.isArray(files)) {
    return [];
  }
  const validFiles = files.filter(hasValidFileKey);
  const filteredCount = files.length - validFiles.length;

  if (filteredCount > 0) {
    console.warn(
      `Filtered out ${filteredCount} deliverable file${
        filteredCount === 1 ? '' : 's'
      } with invalid keys for deliverable "${deliverable?.key ?? 'unknown'}".`
    );
  }

  return validFiles.map((file) => {
    const id = file.key;
    const label =
      typeof file.name === 'string' && file.name.trim().length > 0
        ? file.name
        : 'Columns';
    const { definitions, categories } = getFileCustomFilters(file);
    return {
      id,
      label,
      isDefault: file.default === true,
      columnOptions: getColumnOptionsFromFile(file),
      columnItems: getColumnItemsFromFile(file),
      condition: file.condition,
      hasFilters: getFileHasFilters(file),
      supportsCustomColumns: getFileSupportsCustomColumns(file),
      customFilterDefinitions: definitions,
      customFilterCategories: categories,
    };
  });
}

export type StringFilterDefinition = {
  name: string;
  key: string;
  type: 'string';
  operators: StringFilterOperator[];
  modifiers: FilterModifier[];
  condition?: DataBuilderCondition | null;
  isSeparate: boolean;
  groupKey?: string | null;
};

export type SelectionListFilterDefinition = {
  name: string;
  selectionList: string;
  keys: string[];
  type: 'selection_list';
  operators: SelectionListFilterOperator[];
  modifiers: FilterModifier[];
  condition?: DataBuilderCondition | null;
  isSeparate: boolean;
  groupKey?: string | null;
};

export type NumericFilterDefinition = {
  name: string;
  key: string;
  type: 'numeric';
  operators: NumericFilterOperator[];
  modifiers: FilterModifier[];
  condition?: DataBuilderCondition | null;
  isSeparate: boolean;
  groupKey?: string | null;
};

export type DateFilterDefinition = {
  name: string;
  key: string;
  type: 'date';
  operators: DateFilterOperator[];
  modifiers: FilterModifier[];
  condition?: DataBuilderCondition | null;
  isSeparate: boolean;
  groupKey?: string | null;
};

export type BooleanFilterDefinition = {
  name: string;
  key: string;
  type: 'boolean';
  operators: BooleanFilterOperator[];
  modifiers: FilterModifier[];
  condition?: DataBuilderCondition | null;
  isSeparate: boolean;
  groupKey?: string | null;
};

export type CustomFilterDefinition =
  | StringFilterDefinition
  | SelectionListFilterDefinition
  | NumericFilterDefinition
  | DateFilterDefinition
  | BooleanFilterDefinition;

export type CustomFilterCategory = {
  name: string;
  key: string;
  description?: string | null;
  condition?: DataBuilderCondition | null;
  filters: CustomFilterDefinition[];
};

function parseFilterDefinitionCondition(
  item: unknown
): DataBuilderCondition | null | undefined {
  if (typeof item !== 'object' || item === null || !('condition' in item)) {
    return undefined;
  }
  const c = (item as { condition?: DataBuilderCondition | null }).condition;
  return c === undefined ? undefined : c;
}

function parseSeparateFlag(item: unknown): boolean {
  if (typeof item !== 'object' || item === null || !('separate' in item)) {
    return false;
  }
  return (item as { separate?: boolean | null }).separate === true;
}

/**
 * Combines a parent group condition with a child filter condition under an
 * implicit AND since both conditions must be true for the filter to be shown.
 */
export function mergeConditions(
  parent: DataBuilderCondition | null | undefined,
  child: DataBuilderCondition | null | undefined
): DataBuilderCondition | null | undefined {
  const parentPresent = parent !== null && parent !== undefined;
  const childPresent = child !== null && child !== undefined;
  if (!parentPresent && !childPresent) return undefined;
  if (!parentPresent) return child;
  if (!childPresent) return parent;
  return {
    type: 'and',
    conditions: [parent, child],
  };
}

function parseLeafFilterDefinition(
  item: unknown,
  parentCondition?: DataBuilderCondition | null,
  parentGroupKey?: string | null
): CustomFilterDefinition | null {
  const ownCondition = parseFilterDefinitionCondition(item);
  const condition = mergeConditions(parentCondition, ownCondition);
  const isSeparate = parseSeparateFlag(item);
  const groupKey = parentGroupKey ?? null;

  if (isApiStringFilter(item)) {
    return {
      name: item.name,
      key: item.key,
      type: 'string',
      operators: (item.operators ?? []) as StringFilterOperator[],
      modifiers: item.modifiers ?? [],
      condition,
      isSeparate,
      groupKey,
    };
  }
  if (isApiSelectionListFilter(item)) {
    return {
      name: item.name,
      selectionList: item.selection_list,
      keys: item.keys,
      type: 'selection_list',
      operators: (item.operators ?? []) as SelectionListFilterOperator[],
      modifiers: item.modifiers ?? [],
      condition,
      isSeparate,
      groupKey,
    };
  }
  if (isApiNumericFilter(item)) {
    return {
      name: item.name,
      key: item.key,
      type: 'numeric',
      operators: (item.operators ?? []) as NumericFilterOperator[],
      modifiers: item.modifiers ?? [],
      condition,
      isSeparate,
      groupKey,
    };
  }
  if (isApiDateFilter(item)) {
    return {
      name: item.name,
      key: item.key,
      type: 'date',
      operators: (item.operators ?? []) as DateFilterOperator[],
      modifiers: item.modifiers ?? [],
      condition,
      isSeparate,
      groupKey,
    };
  }
  if (isApiBooleanFilter(item)) {
    return {
      name: item.name,
      key: item.key,
      type: 'boolean',
      operators: (item.operators ?? []) as BooleanFilterOperator[],
      modifiers: item.modifiers ?? [],
      condition,
      isSeparate,
      groupKey,
    };
  }
  return null;
}

type ParsedCustomFilters = {
  definitions: CustomFilterDefinition[];
  categories: CustomFilterCategory[];
};

/**
 * Single-pass parser that walks the API `filters` array once and returns
 * both the flat list of all custom filter definitions and the categorized
 * grouping for the dropdown. Replaces the older split parsers that walked
 * the array twice.
 *
 * Behavior:
 * - Top-level (non-group) filters are added to `definitions` only.
 * - Group children are parsed once and pushed into both `definitions`
 *   (carrying the merged group/leaf condition) and the corresponding
 *   `category.filters`, except defs flagged `isSeparate` are hoisted out
 *   of the category (they render as their own card and must not appear
 *   under a group heading).
 * - Categories whose `filters` end up empty after the `isSeparate` hoist
 *   are dropped, so the dropdown never shows an empty group heading.
 */
function parseFilterDefinitionsAndCategories(
  filters: unknown[]
): ParsedCustomFilters {
  const definitions: CustomFilterDefinition[] = [];
  const categories: CustomFilterCategory[] = [];

  for (const item of filters) {
    if (!isApiFilterGroup(item)) {
      const parsed = parseLeafFilterDefinition(item);
      if (parsed !== null) {
        definitions.push(parsed);
      }
      continue;
    }

    const groupCondition = parseFilterDefinitionCondition(item);
    const groupKey = item.key;
    const childDefs: CustomFilterDefinition[] = [];

    for (const child of item.filters) {
      const parsed = parseLeafFilterDefinition(child, groupCondition, groupKey);
      if (parsed === null) continue;
      definitions.push(parsed);
      if (!parsed.isSeparate) {
        childDefs.push(parsed);
      }
    }

    if (childDefs.length === 0) continue;

    categories.push({
      name: item.name,
      key: groupKey,
      description: item.description ?? undefined,
      condition: groupCondition,
      filters: childDefs,
    });
  }

  return { definitions, categories };
}

// Composite id used by the modals to address a def in NestedFilterSelect.
// The bare key/selectionList alone isn't unique because two defs in different
// groups can share it (e.g. two "country" selection lists with different
// display names). Namespacing by groupKey gives every option a unique id.
export function getFilterDefId(def: CustomFilterDefinition): string {
  const baseKey = def.type === 'selection_list' ? def.selectionList : def.key;
  return def.groupKey ? `${def.groupKey}::${baseKey}` : baseKey;
}

function defMatchesField(def: CustomFilterDefinition, field: string): boolean {
  if (def.type === 'selection_list') {
    return def.selectionList === field || def.keys.includes(field);
  }
  return def.key === field;
}

export function findFilterDefByField(
  field: string,
  defs: CustomFilterDefinition[],
  groupKey?: string | null
): CustomFilterDefinition | undefined {
  if (groupKey !== undefined && groupKey !== null) {
    const grouped = defs.find(
      (def) => def.groupKey === groupKey && defMatchesField(def, field)
    );
    if (grouped) return grouped;
  }
  return defs.find((def) => defMatchesField(def, field));
}

/**
 * Build a map from raw condition field keys to human-readable labels.
 * All granularity keys (e.g. role_k10, role_k50) map to the definition
 * name (e.g. "Role"). Granularity suffixes are shown on values instead.
 */
export function buildFieldLabelMap(
  defs: CustomFilterDefinition[]
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const def of defs) {
    if (def.type === 'selection_list') {
      for (const key of def.keys) {
        map[key] = def.name;
      }
      map[def.selectionList] = def.name;
    } else {
      map[def.key] = def.name;
    }
  }
  return map;
}

/**
 * Build a map from raw condition field keys to short granularity suffixes.
 * e.g. { "role_k10": "k10", "role_k50": "k50" }
 * Geography fields are excluded (obvious enough without).
 */
export function buildFieldGranularityMap(
  defs: CustomFilterDefinition[]
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const def of defs) {
    if (def.type !== 'selection_list') continue;
    if (def.selectionList === 'geo' || def.name.toLowerCase() === 'geography') {
      continue;
    }
    const prefix = def.selectionList + '_';
    for (const key of def.keys) {
      if (key.startsWith(prefix)) {
        map[key] = key.slice(prefix.length);
      }
    }
  }
  return map;
}

function isApiStringFilter(item: unknown): item is ApiStringFilter {
  return (
    typeof item === 'object' &&
    item !== null &&
    'type' in item &&
    (item as Record<string, unknown>).type === 'string'
  );
}

function isApiSelectionListFilter(
  item: unknown
): item is ApiSelectionListFilter {
  return (
    typeof item === 'object' &&
    item !== null &&
    'type' in item &&
    (item as Record<string, unknown>).type === 'selection_list'
  );
}

function isApiNumericFilter(item: unknown): item is ApiNumericFilter {
  return (
    typeof item === 'object' &&
    item !== null &&
    'type' in item &&
    (item as Record<string, unknown>).type === 'numeric'
  );
}

function isApiDateFilter(item: unknown): item is ApiDateFilter {
  return (
    typeof item === 'object' &&
    item !== null &&
    'type' in item &&
    (item as Record<string, unknown>).type === 'date'
  );
}

function isApiBooleanFilter(item: unknown): item is ApiBooleanFilter {
  return (
    typeof item === 'object' &&
    item !== null &&
    'type' in item &&
    (item as Record<string, unknown>).type === 'boolean'
  );
}

function isApiFilterGroup(item: unknown): item is ApiFilterGroup {
  if (typeof item !== 'object' || item === null) {
    return false;
  }
  const r = item as Record<string, unknown>;
  return (
    r.type === 'group' && Array.isArray(r.filters) && typeof r.key === 'string'
  );
}

const EMPTY_PARSED_CUSTOM_FILTERS: ParsedCustomFilters = {
  definitions: [],
  categories: [],
};

export function getCustomFilters(
  deliverable: ApiDeliverable | undefined
): ParsedCustomFilters {
  const filters = deliverable?.filters;
  if (!Array.isArray(filters)) {
    return EMPTY_PARSED_CUSTOM_FILTERS;
  }
  return parseFilterDefinitionsAndCategories(filters);
}

export function getFileCustomFilters(
  file: ApiFile | undefined
): ParsedCustomFilters {
  const filters = file?.filters;
  if (!Array.isArray(filters)) {
    return EMPTY_PARSED_CUSTOM_FILTERS;
  }
  return parseFilterDefinitionsAndCategories(filters);
}

/**
 * Finds the single existing CustomFilter that corresponds to a given
 * separate-filter definition.
 *
 * Contract: only valid for defs where `def.isSeparate === true`. Separate
 * filters render as their own card in the UI and are constructed with a
 * single condition locked to that def's field, so matching only against
 * `conditions[0]?.field` is sufficient. Calling this with a regular
 * (non-separate) def will silently match only on the first condition and
 * is not supported.
 */
export function findSeparateCustomFilterForDef(
  def: CustomFilterDefinition,
  filters: CustomFilter[]
): CustomFilter | undefined {
  const matchField =
    def.type === 'selection_list' ? def.selectionList : def.key;
  return filters.find((f) => f.conditions[0]?.field === matchField);
}

/**
 * Multi-condition filters that touch any `isSeparate` definition must be
 * AND-combined; OR is not a valid combination for separate fields. The
 * modal is intentionally unaware of separate-ness, so callers run the
 * emitted filter through this guard before persisting.
 */
export function enforceSeparateBinary(
  filter: CustomFilter,
  defs: CustomFilterDefinition[]
): CustomFilter {
  if (filter.conditions.length <= 1) return filter;
  const bindsSeparate = filter.conditions.some((condition) => {
    const def = findFilterDefByField(condition.field, defs, condition.groupKey);
    return def?.isSeparate === true;
  });
  if (!bindsSeparate) return filter;
  return { ...filter, binary: 'AND' };
}
